import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import i18n from "@/lib/i18n";
import { showToast } from "@/features/shared/components";
import { UserAccount, UserInviteBody, api } from "@/lib/api";
import { unwrapAxiosData } from "@/lib/api/query-utils";

export const keys = {
  usersByOrganisation: (organisationId: string) => ["users", organisationId] as const,
};

export const useUsersByOrganisationQuery = (organisationId: string) => {
  return useQuery({
    queryKey: keys.usersByOrganisation(organisationId),
    queryFn: (): Promise<UserAccount[]> =>
      unwrapAxiosData(api.organizations.getUserAccountsForOrganization(organisationId)),
  });
};

export const useAddUserMutation = (organisationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (user: UserInviteBody) => {
      return api.users.inviteUserToOrganization(organisationId, user);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.usersByOrganisation(organisationId) });
      showToast(i18n.t("admin.users.add_user.toast.success"));
    },
    onError: () => {
      showToast(i18n.t("admin.users.add_user.toast.error"), { type: "error" });
    },
  });
};

export const useDeleteUserMutation = (organisationId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      return api.users.deleteUserFromOrganization(organisationId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.usersByOrganisation(organisationId) });
      showToast(i18n.t("admin.users.delete_user.toast.success"));
    },
    onError: () => {
      showToast(i18n.t("admin.users.delete_user.toast.error"), { type: "error" });
    },
  });
};
